import moment from 'moment';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {assert, assertAndContinue} from 'Asserts';
import reactUpdate from 'immutability-helper';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';
import {v4 as uuidv4} from 'uuid';
import {NotebookSectionColor} from 'graphql-client/__generated/graphqlGlobalTypes';

export function makeDay(dateString, titleString, notePartIndex, provenanceUuid, sectionColor) {
  return {
    date: dateString,
    index: notePartIndex,
    title: titleString,
    cid: _.uniqueId('entryDay'),
    provenanceUuid: provenanceUuid ?? uuidv4(),
    sectionColor: sectionColor ?? NotebookSectionColor.DEFAULT,
    noteVersionCid: _.uniqueId('note-version'),
  };
}

export function fillEmptyDays(note) {
  // Finds the first occurrence of an empty day, updates the note parts accordingly, then
  // recurses.
  for (let i = 0, end = note.days.length, asc = 0 <= end; asc ? i < end : i > end; asc ? i++ : i--) {
    let day = note.days[i];
    const nextDay = note.days[i + 1];
    if (nextDay != null && day.index !== nextDay.index) {
      continue;
    }
    if (nextDay == null && day.index !== note.noteParts.length) {
      continue;
    }

    // Fill in note parts in between days with the same index, and days at the end with no noteParts.
    const {index} = day;
    const newNoteParts = reactUpdate(note.noteParts, {$splice: [[index, 0, makeDefaultItem()]]});
    const newDays = note.days.slice(0, i + 1);
    for (day of Array.from(note.days.slice(i + 1))) {
      newDays.push(
        reactUpdate(day, {
          index: {
            $apply(x) {
              return x + 1;
            },
          },
        })
      );
    }
    note = reactUpdate(note, {$merge: {noteParts: newNoteParts, days: newDays}});
    return fillEmptyDays(note);
  }
  return note;
}

export function formatDate(momentDate) {
  return momentDate.format('YYYY-MM-DD');
}

export function insertDay(note, notePartIndex, fileType, getTextForDate, title) {
  let newMomentDate;
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }
  assert(notePartIndex > 0, "insertDay doesn't support inserting at beginning yet");
  const dayIndex = _.sortedIndex(note.days, {index: notePartIndex}, (day) => day.index);

  // We can't insert at the beginning, so there should always be a previousDay.
  const previousDay = note.days[dayIndex - 1];
  const nextDay = note.days[dayIndex];

  const previousMomentDate = moment(previousDay.date);
  // Not sure if there's a better way to get this, don't want to deal with timezones.
  const currentMomentDate = moment().startOf('day');

  // Try to insert today if possible, otherwise insert previous day + 1.
  if (
    previousMomentDate.isBefore(currentMomentDate) &&
    !APIUtil.isEntryOrSubOrWorkflowTemplateFileType(fileType) &&
    (nextDay == null || currentMomentDate.isBefore(nextDay.date))
  ) {
    newMomentDate = moment();
  } else {
    newMomentDate = previousMomentDate.clone().add(1, 'day');
  }

  if (nextDay != null && !newMomentDate.isBefore(nextDay.date)) {
    notifications.warn(
      `Date cannot be inserted between ${getTextForDate(previousDay.date)} and ${getTextForDate(
        nextDay.date
      )}`
    );
    return note;
  }

  const newDate = formatDate(newMomentDate);
  const newDay = makeDay(newDate, title, notePartIndex);
  const newDays = reactUpdate(note.days, {$splice: [[dayIndex, 0, newDay]]});
  note = reactUpdate(note, {$merge: {days: newDays}});
  note = fillEmptyDays(note);
  return note;
}

export function getPreviousAndNextDateFromIndex({note, dayIndex, notePartIndex, nextDayOffset = 0}) {
  const dayIndexToUse =
    dayIndex != null ? dayIndex : _.sortedIndex(note.days, {index: notePartIndex}, (day) => day.index);

  let previousDate = '';
  let idx = dayIndexToUse - 1;
  while (idx >= 0) {
    previousDate = note.days[idx]?.date;
    if (previousDate) {
      break;
    }
    idx -= 1;
  }

  let nextDate = '';
  idx = dayIndexToUse + nextDayOffset;
  while (idx < note.days.length) {
    nextDate = note.days[idx]?.date;
    if (nextDate) {
      break;
    }
    idx += 1;
  }

  return {previousDate, nextDate};
}

function _checkDateIsBetween(dateAsMoment, startDate, endDate, getTextForDate, inclusive = false) {
  if (!dateAsMoment) {
    return '';
  }
  if (inclusive) {
    if (startDate && dateAsMoment.isBefore(startDate, 'day')) {
      return `Date cannot be inserted before ${getTextForDate(startDate)}`;
    }
    if (endDate && dateAsMoment.isAfter(endDate, 'day')) {
      return `Date cannot be inserted after ${getTextForDate(endDate)}`;
    }
  } else {
    if (startDate && dateAsMoment.isSameOrBefore(startDate, 'day')) {
      return `Date cannot be inserted before ${getTextForDate(startDate)}`;
    }
    if (endDate && dateAsMoment.isSameOrAfter(endDate, 'day')) {
      return `Date cannot be inserted after ${getTextForDate(endDate)}`;
    }
  }
  return '';
}

export function insertGivenDay(note, notePartIndex, fileType, getTextForDate, dateString, title) {
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }

  const dayIndex = _.sortedIndex(note.days, {index: notePartIndex}, (day) => day.index);
  const {previousDate, nextDate} = getPreviousAndNextDateFromIndex({note, dayIndex});

  if (!dateString && !title) {
    notifications.warn('Must have a date or title');
    return note;
  }

  const newMomentDate = dateString ? moment(dateString) : null;
  const dateError = _checkDateIsBetween(newMomentDate, previousDate, nextDate, getTextForDate, true);
  if (dateError) {
    notifications.warn(dateError);
    return note;
  }

  const newDate = newMomentDate ? formatDate(newMomentDate) : '';
  const newDay = makeDay(newDate, title, notePartIndex);
  const newDays = reactUpdate(note.days, {$splice: [[dayIndex, 0, newDay]]});
  note = reactUpdate(note, {$merge: {days: newDays}});
  note = fillEmptyDays(note);
  return note;
}

export function deleteDay(note, cid) {
  // eslint-disable-next-line no-restricted-syntax
  const dayIndex = _.findIndex(note.days, {cid});
  if (note.days[dayIndex].index === 0) {
    assertAndContinue(false, "Can't delete first entry day");
    return note;
  } else {
    return reactUpdate(note, {days: {$splice: [[dayIndex, 1]]}});
  }
}

export function updateTitle(note, cid, newTitle, fileType) {
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }
  // eslint-disable-next-line no-restricted-syntax
  const dayIndex = _.findIndex(note.days, {cid});
  const day = note.days[dayIndex];

  if (!day.date && !newTitle) {
    notifications.warn('Must have a date or title');
    return note;
  }

  const updatedDay = reactUpdate(day, {$merge: {title: newTitle}});
  const updatedDays = reactUpdate(note.days, {$splice: [[dayIndex, 1, updatedDay]]});
  note = reactUpdate(note, {$merge: {days: updatedDays}});

  return note;
}

export function updateColor(note, cid, newColor) {
  const dayIndex = note.days.findIndex((day) => day.cid === cid);
  const day = note.days[dayIndex];

  if (day.sectionColor === newColor) {
    return note;
  }

  const updatedDay = reactUpdate(day, {$merge: {sectionColor: newColor}});
  const updatedDays = reactUpdate(note.days, {$splice: [[dayIndex, 1, updatedDay]]});
  note = reactUpdate(note, {$merge: {days: updatedDays}});

  return note;
}

export function updateDay(note, cid, newTitle, newDate, getTextForDate, fileType, requireDate = true) {
  if (fileType == null) {
    fileType = ModelTypeConstants.ENTRY;
  }
  // eslint-disable-next-line no-restricted-syntax
  const dayIndex = _.findIndex(note.days, {cid});
  const day = note.days[dayIndex];
  if (requireDate && !newDate) {
    newDate = day.date;
  }
  if (day.date === newDate && day.title === newTitle) {
    return note;
  }

  const newMomentDate = newDate ? moment(newDate) : null;
  let previousDate = note.days[dayIndex - 1]?.date;
  let nextDate = note.days[dayIndex + 1]?.date;
  let checkDateBoundsInclusive = false;
  if (!requireDate) {
    checkDateBoundsInclusive = true;
    ({previousDate, nextDate} = getPreviousAndNextDateFromIndex({note, dayIndex, nextDayOffset: 1}));

    if (!newDate && !newTitle) {
      notifications.warn('Must have a date or title');
      return note;
    }
  }
  const dateError = _checkDateIsBetween(
    newMomentDate,
    previousDate,
    nextDate,
    getTextForDate,
    checkDateBoundsInclusive
  );
  if (dateError) {
    notifications.warn(dateError);
    return note;
  }

  const newDay = reactUpdate(day, {$merge: {date: newDate, title: newTitle}});
  const newDays = reactUpdate(note.days, {$splice: [[dayIndex, 1, newDay]]});
  note = reactUpdate(note, {$merge: {days: newDays}});
  return note;
}

export function updateDaysForReplace(note, start, end, insertItemCount) {
  const newEnd = start + insertItemCount;
  const change = newEnd - end;
  const newDays = note.days.map((day) => {
    if (start < day.index && day.index <= end) {
      day = reactUpdate(day, {$merge: {index: newEnd}});
    } else if (day.index > end || (start === end && end === day.index && day.index !== 0)) {
      // Inserting at the junction between two days should default to inserting
      // on a previous day. This is used whenever you e.g. Insert Table on the
      // last note part in a day.
      day = reactUpdate(day, {$merge: {index: day.index + change}});
    }
    return day;
  });

  note = reactUpdate(note, {$merge: {days: newDays}});
  note = fillEmptyDays(note);
  return note;
}
